// Copyright (c) 2026 Solartek LLC. All rights reserved.

import { Suspense } from 'react';
import type { RouteObject } from 'react-router';
import type { Permission } from '@flowza/contracts';
import { RequirePermission } from '@/components/layout/protected-route';
import { registerNamespace } from '@/lib/i18n-namespace';
import en from '@/locales/en/employees.json';
import ar from '@/locales/ar/employees.json';
import { EmployeeImportPage, EmployeeNewPage, EmployeeProfilePage, EmployeesListPage, OrgChartPage, PageFallback } from './pages/lazy';

registerNamespace('employees', en, ar);

const wrap = (perms: Permission[], node: React.ReactNode, any = false) => <RequirePermission permissions={perms} any={any}><Suspense fallback={<PageFallback />}>{node}</Suspense></RequirePermission>;

/**
 * Routes for the employees feature (lazy pages, permission-gated; the server enforces permissions again). The directory
 * list stays an HR surface (employee.view); a profile also opens for a line manager (employee.view_team) — the API/RLS
 * answer 404 for anybody who is not their own record or a direct report.
 */
export const employeesRoutes: RouteObject[] = [
  { path: 'employees', element: wrap(['employee.view'], <EmployeesListPage />) },
  { path: 'employees/new', element: wrap(['employee.create'], <EmployeeNewPage />) },
  { path: 'employees/import', element: wrap(['employee.import'], <EmployeeImportPage />) },
  // the organisation-wide reporting lines (a static segment: React Router ranks it above employees/:id)
  { path: 'employees/org-chart', element: wrap(['employee.view'], <OrgChartPage />) },
  { path: 'employees/:id', element: wrap(['employee.view', 'employee.view_team'], <EmployeeProfilePage />, true) },
];
